/**
 * Pure, DOM-free helpers for classifying manifest sources and bindings, shared
 * by validate.ts and compile.ts: the feed-binding grammar, the default target
 * rule, the source×target matrix, and the effective produced-kind of a slot.
 *
 * The submit-tool property name for an agent slot is simply the slot id.
 * Agent-slot `references` reuse the same feed-binding grammar; compile and
 * validate walk them with `parseFeedBinding` like any feed-bound slot.
 */

import type {
  FeedBindingKind,
  ProducedKind,
  ProductImageSlotName,
  Slot,
  SlotTarget,
} from "./types";

// ---------------------------------------------------------------------------
// Feed binding grammar: title | description | user_tags.<key> |
//   content.images[primary|additional-N]
// ---------------------------------------------------------------------------

export type ParsedFeedBinding =
  | { kind: "field"; field: "title" | "description" }
  | { kind: "userTag"; key: string }
  | { kind: "image"; image: "primary" | { additional: number } };

const USER_TAG_RE = /^user_tags\.([^.\s][^\s]*)$/;
const IMAGE_RE = /^content\.images\[(primary|additional-(\d+))\]$/;

export function parseFeedBinding(binding: string): ParsedFeedBinding | null {
  if (binding === "title" || binding === "description") {
    return { kind: "field", field: binding };
  }
  const userTag = USER_TAG_RE.exec(binding);
  if (userTag) return { kind: "userTag", key: userTag[1]! };

  const image = IMAGE_RE.exec(binding);
  if (image) {
    if (image[1] === "primary") return { kind: "image", image: "primary" };
    return { kind: "image", image: { additional: Number(image[2]) } };
  }
  return null;
}

export function feedBindingKind(binding: string): FeedBindingKind | null {
  const parsed = parseFeedBinding(binding);
  if (!parsed) return null;
  return parsed.kind === "image" ? "image" : "text";
}

/** Build the `user_tags.<key>` feed binding for a tag key (parseFeedBinding's inverse). */
export function userTagBinding(key: string): string {
  return `user_tags.${key}`;
}

/** Build the `content.images[<slot>]` feed binding for a product image slot. */
export function imageBinding(slot: ProductImageSlotName): string {
  return `content.images[${slot}]`;
}

// ---------------------------------------------------------------------------
// Default target + source×target matrix + effective produced kind
// ---------------------------------------------------------------------------

/**
 * Resolves the effective target for a slot. Explicit target wins. Otherwise:
 * an <img> element defaults to `attr:src`; everything else defaults to
 * `content`.
 */
export function defaultTarget(slot: Slot, tagName: string | null): SlotTarget {
  if (slot.target) return slot.target;
  if (tagName === "img") return { kind: "attr", name: "src" };
  return { kind: "content" };
}

/**
 * The produced kind of a slot, given its resolved target:
 *   - feed: derived from the binding (text | image)
 *   - agent: explicit `produces.kind` if set, else derived from the target
 *     (content→text, attr:src→image).
 */
export function effectiveProduces(
  slot: Slot,
  target: SlotTarget,
): ProducedKind | null {
  if (slot.source.from === "feed") {
    return feedBindingKind(slot.source.binding);
  }
  if (slot.source.produces) return slot.source.produces.kind;
  switch (target.kind) {
    case "content":
      return "text";
    case "attr":
      return "image";
  }
}

/**
 * The source×target matrix:
 *   text  → content
 *   image → attr:src
 */
export function isTargetValidForProduced(
  produced: ProducedKind,
  target: SlotTarget,
): boolean {
  switch (produced) {
    case "text":
      return target.kind === "content";
    case "image":
      return target.kind === "attr" && target.name === "src";
  }
}
